import { IPaymentOptionAccordionItem } from 'widgets/checkout/PaymentAccordionItem';
import { submitFormJson } from 'widgets/toolbox/ajax';
import { get } from 'widgets/toolbox/util';

import { TWidget } from 'widgets/Widget';

import { TBasicInputInstance } from 'widgets/forms/BasicInput';
import { TInputCheckboxInstance } from 'widgets/forms/InputCheckbox';
import { TLabelInstance } from 'widgets/global/Label';
import { TPaymentAccordionInstance } from 'widgets/checkout/PaymentAccordion';

/**
 * @param AddressCheckoutForm Base widget for extending
 * @returns Billing Form
 */
function BillingFormClassCreator(AddressCheckoutForm: import('widgets/checkout/AddressCheckoutForm').TAddressCheckoutForm) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class BillingForm
     * @augments AddressCheckoutForm
     * @classdesc Represents checkout billing form with next features:
     * 1. Handles form submission
     * 2. Handles form submission server response
     * 3. Clears form sensitive data
     * 4. Toggles use shipping address functionality
     * 5. Prepares address options for an address list
     *
     * Widget has next relationship:
     * * {@link PaymentAccordion#validate} is used for payment validation before form submission
     * * {@link PaymentAccordion#process} is used before form submission to get payment method
     * * {@link PaymentAccordion#cleanupSensitiveData} is used to clear sensitive payment data
     * * {@link PaymentAccordion#handleError} is used to show payment method errors
     * * {@link Label#toggle} is used to show/hide use shipping block summary
     * * {@link InputCheckbox#getValue} is used to get state of `use shipping address` checkbox
     * @example <caption>Checkout billing form</caption>
     * <form
     *     class="b-form"
     *     autocomplete="on"
     *     novalidate
     *     method="POST"
     *     action="${URLUtils.url('CheckoutServices-SubmitPayment')}"
     *     data-widget="billingForm"
     *     data-use-address-type="${!empty(pdict.customer.addresses) ? 'useBilling' : 'useShipping'}"
     *     data-address-selector="billingAddressSelector"
     *     data-widget-event-submit="billingSubmitted"
     *     data-forward-to-parent="submitCurrentStep:toggleNextPaymentButton"
     *     data-address-fields="${pdict.billingAddressFields.join(',')}"
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'billing')}"
     *     data-tau="checkout_billing"
     *     <isprint value=${pdict.forms.billingForm.attributes} encoding="off" />
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('addressSuggestionProvider').value}"
     *>
     *    <fieldset class="b-form_set">
     *      <legend
     *          class="b-form_set-label" ${pdict.order.totals.isZeroTotal || pdict.hidePaymentOptions ? 'hidden' : ''}
     *          data-ref="paymentOptionsLegend"
     *      >
     *          ${Resource.msg('billing.fieldset.paymentchoose', 'checkout', null)}
     *      </legend>
     *      <div
     *          role="alert"
     *          class="b-form-message b-message m-error"
     *          hidden="hidden"
     *          data-ref="errorMessageLabel"
     *      ></div>
     *      <div
     *          class="b-form-message b-message m-error"
     *          hidden="hidden"
     *          data-ref="paymentErrorMessage"
     *          role="alert"
     *      ></div>
     *
     *      <isinclude template="checkout/billing/paymentOptions" />
     *    </fieldset>
     *
     *    <div
     *        class="b-billing"
     *        data-ref="billingFormWrapper"
     *        hidden="hidden"
     *    >
     *      ... billing form content
     *    </div>
     * </form>
     */
    class BillingForm extends AddressCheckoutForm {
        prefs() {
            return {
                errorMessageLabelRef: 'paymentErrorMessage',
                simpleShipping: false,
                skipSummaryStep: false,
                useAddressType: 'useShipping',
                saveAddressCheckbox: 'dwfrm_billing_addressFields_saveAddress',
                zeroPaymentClass: 'm-zero_payment',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization
         */
        init(): void {
            super.init();

            this.eventBus().on('order.updated', 'onOrderUpdated');
        }

        /**
         * @description Submit form handler
         */
        submit(): void {
            if (this.isValid()) {
                this.handleSubmit();
            } else {
                this.showAddressForm();
                this.validate();
            }
        }

        /**
         * @description Check is Form valid based on Form fields validation
         *
         * We don't need to validate billing form if Payment Method of current Payment Accordion Item
         * has dummy billing address (like Zero Payment or Pay in Store),
         * or has own validation (like PayPal, ApplePay)
         */
        isValid() {
            return this.isBillingFormHasToBeValidated()
                ? super.isValid()
                : true;
        }

        /**
         * @description Check if billing form fields valid
         *
         * We don't need to validate billing form if Payment Method of current Payment Accordion Item
         * has dummy billing address (like Zero Payment or Pay in Store),
         * or has own validation (like PayPal, ApplePay)
         */
        isChildrenValid() {
            return this.isBillingFormHasToBeValidated()
                ? super.isChildrenValid()
                : true;
        }

        /**
         * @description Update blocks visibility based on flag
         * @param order - Order model
         */
        onOrderUpdated(order) {
            this.ref('paymentOptionsLegend').toggle(order.hidePaymentOptions !== true);
        }

        /**
         * @description Filter address fields for payment options that don't require billing address from billing form
         */
        getFormFieldsForSubmission() {
            const formFields = this.getFormFields();
            const currentPaymentOption = this.getCurrentPaymentOptionItem();

            if (!currentPaymentOption) {
                return formFields;
            }

            if (!currentPaymentOption.prefs().skipBillingAddress) {
                return formFields;
            }

            const addressFields = this.getAddressFields();

            return Object.keys(formFields).reduce((accumulator, fieldName) => {
                if (addressFields.indexOf(fieldName) === -1) {
                    accumulator[fieldName] = formFields[fieldName];
                }

                return accumulator;
            }, {});
        }

        /**
         * @description Validate form and process submission
         * @returns Promise object represents server response for billing form submission
         */
        handleSubmit(): Promise<void | Record<string, unknown>> | Promise<null> {
            if (!this.isChildrenValid() || this.submitting) {
                return Promise.resolve(null);
            }

            this.submitting = true;
            this.busy();
            this.ref(this.prefs().submitButton).disable();
            this.ref(this.prefs().errorMessageLabelRef).hide();

            const submissionResult = this.getById('payment', (payment: TPaymentAccordionInstance) => {
                return payment.process({ billingAddressFields: this.getFormFieldsShort() }).then(data => {
                    return submitFormJson(
                        this.getFormUrl(),
                        {
                            ...this.getFormFieldsForSubmission(),
                            ...this.getAdditionalBillingData(),
                            ...data

                        },
                        this.ref('self').attr('method') === 'GET' ? 'GET' : 'POST'
                    );
                })
                    .then((response: TServerResponse) => {
                        this.onSubmitted(response);

                        return response;
                    })
                    .catch((err?: TResponseError|Error) => {
                        if (err instanceof Error) {
                            if (err.message) {
                                this.setError(err.message, true, this.prefs().errorMessageLabelRef);
                            }
                        } else if (err?.errorMessage) {
                            this.setError(err.errorMessage, true, this.prefs().errorMessageLabelRef);
                        }
                    })
                    .finally(this.afterSubmission.bind(this));
            });

            return submissionResult || Promise.resolve(null);
        }

        /**
         * @description Used in mixins to add an additional billing data
         */
        getAdditionalBillingData() {
            return {};
        }

        /**
         * @description Clear payment sensitive data
         * @param stage Stage on which data is going to be cleared
         */
        cleanupSensitiveData(stage: string) {
            this.getById('payment', (payment: TPaymentAccordionInstance) => {
                payment.data('cleanupSensitiveData', stage);
            });
        }

        /**
         * @description Process form submission response
         * @emits "billing.form.submitted"
         * @param data - Server response object
         * @param data.success - is it success
         * @param data.serverErrors - server errors
         * @param data.fieldErrors - field errors
         * @param data.cartError - is it cart error
         * @param data.redirectUrl - redirect url
         */
        onSubmitted(data: TServerResponse) {
            this.eventBus().emit('billing.form.submitted');

            if (!data.success) {
                if (data.serverErrors && data.serverErrors.length) {
                    this.setError(data.serverErrors.join(''), true, this.prefs().errorMessageLabelRef);
                }

                if (data.errorMessage) {
                    this.setError(data.errorMessage, true, this.prefs().errorMessageLabelRef);
                }

                if (data.fieldErrors) {
                    this.handleFieldError(data.fieldErrors);
                }

                if (data.cartError && data.redirectUrl) {
                    window.location.assign(data.redirectUrl);
                }
            } else {
                const billingAddressID = get(data, 'order.billing.billingAddress.address.addressUUID');

                this.setSelectedAddress(billingAddressID);
                super.onSubmitted(data);

                if (!this.prefs().skipSummaryStep) {
                    this.cleanupSensitiveData('submission');
                }

                this.hidePaymentError();
            }
        }

        /**
         * @description Handles form field error
         * @param entries form entries
         */
        handleFieldError(entries: TFieldError | Array<TFieldError>) {
            Object.entries(entries).forEach(([name, errorMsg]) => {
                const item = this.getFormFieldItem(name);

                if (item) {
                    if (this.isFormHidden()) {
                        this.showAddressForm();
                    }

                    item.setError(errorMsg);
                } else {
                    this.getById('payment', (payment: TPaymentAccordionInstance) => {
                        payment.handleError(name, errorMsg);
                    });
                }
            });
        }

        /**
         * @description Returns form item
         * @param id - form item id
         */
        getFormFieldItem(id: string): TBasicInputInstance | null {
            let result: TBasicInputInstance | null = null;

            this.eachChild<TBasicInputInstance>((item) => {
                if (item.id === id) {
                    result = item;
                }
            });

            return result;
        }

        /**
         * @description Activate billing step
         * @param param - Activation params object
         * @param param.order - Order object
         * @param param.customer - Customer object
         */
        activate({ order, customer }) {
            super.activate({ order, customer });
            this.cleanupSensitiveData('activation');
            this.prepareAddressTypesSelector(order, customer);
            this.getById('payment', (paymentAccordion: TPaymentAccordionInstance) => {
                paymentAccordion.ref('self').toggleClass(this.prefs().zeroPaymentClass, order.billing.payment.isZeroTotal);

                paymentAccordion.activate();
            });
        }

        /**
         * @description Prepares correct state for use billing\shipping address checkboxes
         * @param order - customer order object
         * @param customer - customer's object
         */
        prepareAddressTypesSelector(order, customer) {
            this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                if (useAddressTypeCheckbox.getValue()) {
                    if (this.prefs().useAddressType === 'useShipping') {
                        this.setSelectedAddress(this.getShippingAddressID(order));
                    } else if (this.prefs().useAddressType === 'useBilling') {
                        const addresses = this.getAddressOptions(order, customer);
                        const selectedAddress = this.getSelectedAddress(this.filterAddresses(addresses));

                        this.setSelectedAddress(selectedAddress);
                    }
                }
            });
        }

        /**
         * @description Toggle use shipping functionality after address selector activation
         */
        onAddressSelectorActivated() {
            this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                this.toggleUseAddressType(useAddressTypeCheckbox, false);
            });
        }

        /**
         * @description Toggle use shipping or billing functionality after address selector activation
         * @param useAddressTypeCheckbox - Use shipping or billing checkbox component
         * @param isBillingFormShouldBeCleared - Flag to determine if billing form should be cleared when checkbox is unchecked
         */
        toggleUseAddressType(useAddressTypeCheckbox: TInputCheckboxInstance, isBillingFormShouldBeCleared = true) {
            const checked = Boolean(useAddressTypeCheckbox.getValue());
            const isBilling = this.prefs().useAddressType === 'useBilling';

            if (checked) {
                const selectedAddress = isBilling ? this.getBillingAddressID() : this.getShippingAddressID();

                this.setSelectedAddress(selectedAddress);
                this.selectAddressInSelector();
                this.hideAddressFields();
                this.hideSavedAddresses();
                this.fulfillAddressFromAddressSelector();
            } else {
                const preparedAddressOptions = this.getPreparedAddressOptions(this.order, this.customer);
                const addressesLength = preparedAddressOptions.basketAddresses.length + preparedAddressOptions.customerAddresses.length;

                if (addressesLength > 1) {
                    this.hideAddressForm();
                    this.fulfillAddressFromAddressSelector();
                } else {
                    if (isBillingFormShouldBeCleared) {
                        this.clearAddressFields(false);
                    }

                    this.showAddressForm();
                    this.callIfExists('focusFirstInput', 'addressForm'); // class AccessibilityFocusMixin, method focusFirstInput
                }
            }

            this.getById('useAddressTypeDescription', (useAddressTypeDescription: TLabelInstance) => {
                useAddressTypeDescription.toggle(checked);
            });
        }

        /**
         * @description Returns selected address
         * @param filteredAddresses Filtered addresses
         * @returns Sorted addresses
         */
        getSelectedAddress(filteredAddresses: Array<Record<string, string>>): string {
            let selectedBillingAddress;

            if (this.prefs().useAddressType !== 'useBilling') {
                selectedBillingAddress = filteredAddresses.find(address => address.isDefaultBillingAddress);
            } else {
                // @ts-ignore Not proper described order instance
                const billingAddressUUID = this.order?.billing?.billingAddress?.address?.addressUUID;

                if (billingAddressUUID) {
                    selectedBillingAddress = filteredAddresses.find(address => address.addressUUID === billingAddressUUID);
                }

                if (!selectedBillingAddress) {
                    selectedBillingAddress = filteredAddresses.find(address => address.isDefaultBillingAddress);
                }
            }

            return selectedBillingAddress?.addressUUID || filteredAddresses[0].addressUUID;
        }

        /**
         * @description Sort addresses
         * @param addresses Addresses that will be sorted
         * @returns Sorted addresses
         */
        getSortedAddresses(addresses: Array<Record<string, unknown>>): Array<Record<string, unknown>> {
            return addresses.sort((address1, address2) => {
                if ((address1.isDefaultBillingAddress && address2.addressId) || !address1.addressId) {
                    return -1;
                } else if ((address2.isDefaultBillingAddress && address1.addressId) || !address2.addressId) {
                    return 1;
                } else {
                    return 0;
                }
            });
        }

        /**
         * @description Initialize saved addresses selector
         */
        activateAddressSelector(): void {
            this.toggleSaveAddressCheckbox(false);

            if (this.prefs().useAddressType === 'useBilling' || !this.prefs().simpleShipping) {
                this.getById('useAddressTypeDescription', (useAddressTypeDescription: TLabelInstance) => {
                    const data = {
                        order: <Record<string, string>> {},
                        billingSelectedAddress: <Record<string, string>> {}
                    };

                    if (this.prefs().useAddressType === 'useShipping') {
                        data.order = <Record<string, string>> this.order;
                    } else if (this.prefs().useAddressType === 'useBilling') {
                        const addresses = this.getAddressOptions(this.order, this.customer);
                        const billingSelectedAddressUUID = this.getSelectedAddress(this.filterAddresses(addresses));

                        data.billingSelectedAddress = <Record<string, string>>addresses.find((address) => {
                            return address.addressUUID === billingSelectedAddressUUID;
                        });
                    }

                    useAddressTypeDescription.updateTemplate({
                        ...data
                    }).then(() => {
                        this.getById(this.prefs().useAddressType, (useAddressTypeCheckbox: TInputCheckboxInstance) => {
                            useAddressTypeDescription.toggle(Boolean(useAddressTypeCheckbox.getValue()));
                        });
                    });
                });

                super.activateAddressSelector();
            } else {
                const preparedAddressOptions = this.getPreparedAddressOptions(this.order, this.customer);

                if (preparedAddressOptions.customerAddresses.length || preparedAddressOptions.basketAddresses.length) {
                    super.activateAddressSelector();
                } else {
                    this.showAddressForm();
                }
            }
        }

        /**
         * @description toggle Address Form's saveAddress checkbox in Billing Address Form
         * @param state - checkbox on/off
         */
        toggleSaveAddressCheckbox(state: boolean): void {
            this.getById(this.prefs().saveAddressCheckbox, (checkbox: TInputCheckboxInstance) => {
                checkbox.toggleState(state);
            });
        }

        /**
         * @description Handle click on add new address button.
         * Toggle saved and new address blocks
         * @listens dom#click
         */
        addNewAddress(): void {
            super.addNewAddress();
            this.toggleSaveAddressCheckbox(true);
        }

        /**
         * @description Return shipping address ID from order object
         * @param order - order data
         * @returns shipping addresses ID
         */
        getShippingAddressID(order = this.order): string {
            return get(order, 'shipping.0.shippingAddress.addressUUID', '');
        }

        /**
         * @description Return billing address ID
         * @param order - order data
         * @param customer - customer data
         * @returns billing addresses ID
         */
        getBillingAddressID(order = this.order, customer = this.customer): string {
            const addresses = this.getAddressOptions(order, customer);

            return this.getSelectedAddress(this.filterAddresses(addresses));
        }

        /**
         * @override
         * @description Return applicable addresses for current form
         * @param order - order data
         * @param customer - customer data
         * @returns returns billing addresses
         */
        getAddressOptions(order, customer): Array<Record<string, string>> {
            // Return all addresses so guest user could use shipping address as billing
            return this.getAllAddresses(order, customer);
        }

        /**
         * @description Clear payment error
         */
        hidePaymentError() {
            this.clearError(this.prefs().errorMessageLabelRef);
        }

        /**
         * @description Show payment error
         * @param widget - Widget emitted event
         * @param err - Error msg
         */
        showPaymentError(widget: TWidget, err: string) {
            if (!err) {
                return;
            }

            this.setError(err, true, this.prefs().errorMessageLabelRef);
        }

        /**
         * @description Toggle Billing Form state based on passed state
         * @param childWidget Widget Instance
         * @param display State to be set
         * @listens togglebillingform
         */
        toggleBillingForm(childWidget: TWidget | null, display: boolean | undefined) {
            this.ref('billingFormWrapper').toggle(display);
            this.hidePaymentError();
        }

        /**
         * @description Returns payment option accordion items
         */
        getPaymentOptionItems() {
            let items: Array<IPaymentOptionAccordionItem> = [];

            this.getById('payment', (payment: TPaymentAccordionInstance) => {
                items = <Array<IPaymentOptionAccordionItem>> payment.items;
            });

            return items;
        }

        /**
         * @description Returns current open payment option accordion item
         */
        getCurrentPaymentOptionItem() {
            return this.getPaymentOptionItems().find(item => item.isPanelOpen);
        }

        /**
         * @description Checks if the current Payment Accordion item should be validated based on preferences of the payment option widget
         */
        isBillingFormHasToBeValidated(): boolean {
            const currentPaymentOption = this.getCurrentPaymentOptionItem();

            if (currentPaymentOption) {
                return !currentPaymentOption.prefs().skipBillingAddress;
            }

            return false;
        }
    }

    return BillingForm;
}

export type TBillingForm = ReturnType<typeof BillingFormClassCreator>;

export type TBillingFormInstance = InstanceType<TBillingForm>;

export default BillingFormClassCreator;
